import { createContext, useContext, ReactNode } from 'react'
import { LotesProvider, useLotes } from './hooks/useLotes'

interface LotesTableContextType {
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  lotes: unknown[]
  isLoading: boolean
  skeletonRowCount: number
  isFetching: boolean
  isError: boolean
  refetch: () => void
  totalItems: number
  totalPages: number
}

const LotesTableContext = createContext<LotesTableContextType | undefined>(
  undefined
)

interface LotesTableContextProviderProps {
  children: ReactNode
  inventoryId: string
}

function LotesTableContextInner({ children }: { children: ReactNode }) {
  const {
    lotes,
    isLoading,
    isFetching,
    isError,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    refetch,
    totalItems,
    totalPages,
  } = useLotes()

  const skeletonRowCount = pageSize

  return (
    <LotesTableContext.Provider
      value={{
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        lotes,
        isLoading,
        skeletonRowCount,
        isFetching,
        isError,
        refetch,
        totalItems,
        totalPages,
      }}
    >
      {children}
    </LotesTableContext.Provider>
  )
}

export function LotesTableContextProvider({
  children,
  inventoryId,
}: LotesTableContextProviderProps) {
  return (
    <LotesProvider inventoryId={inventoryId}>
      <LotesTableContextInner>{children}</LotesTableContextInner>
    </LotesProvider>
  )
}

export function useLotesTableContext() {
  const context = useContext(LotesTableContext)
  if (context === undefined) {
    throw new Error(
      'useLotesTableContext must be used within a LotesTableContextProvider'
    )
  }
  return context
}
